/**
 * Generic WebSocket Hook
 * Provides WebSocket connection management for any namespace
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';

interface UseWebSocketOptions {
  /** Socket.IO namespace (e.g., '/chat', '/kanban', '/tickets') */
  namespace?: string;
  /** Enable connection (useful for conditional connections) */
  enabled?: boolean;
  /** Auto-connect on mount */
  autoConnect?: boolean;
  /** Additional Socket.IO options */
  socketOptions?: any;
}

interface UseWebSocketReturn {
  /** Socket.IO instance */
  socket: Socket | null;
  /** Connection status */
  connected: boolean;
  /** Manually connect to WebSocket */
  connect: () => void;
  /** Manually disconnect from WebSocket */
  disconnect: () => void;
  /** Reconnect with fresh token */
  reconnect: () => void;
}

/**
 * Generic WebSocket hook for any namespace
 *
 * @example
 * ```typescript
 * const { socket, connected } = useWebSocket({ namespace: '/chat' });
 *
 * useEffect(() => {
 *   if (!socket || !connected) return;
 *
 *   socket.on('some.event', (data) => {
 *     console.log('Event received:', data);
 *   });
 *
 *   return () => {
 *     socket.off('some.event');
 *   };
 * }, [socket, connected]);
 * ```
 */
export function useWebSocket(
  options: UseWebSocketOptions = {}
): UseWebSocketReturn {
  const {
    namespace = '',
    enabled = true,
    autoConnect = true,
    socketOptions = {},
  } = options;

  const [connected, setConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  // Get WebSocket URL from environment variable
  const getWebSocketUrl = useCallback(() => {
    console.log('🔍 DEBUG: VITE_WS_URL =', import.meta.env.VITE_WS_URL);
    console.log('🔍 DEBUG: VITE_API_URL =', import.meta.env.VITE_API_URL);

    // If VITE_WS_URL is set, use it
    if (import.meta.env.VITE_WS_URL) {
      console.log('🔍 Using VITE_WS_URL:', import.meta.env.VITE_WS_URL);
      return import.meta.env.VITE_WS_URL;
    }

    // Get API URL
    const apiUrl =
      import.meta.env.VITE_API_URL || 'http://api.thilo.loc:8000/v2/api';

    console.log('🔍 DEBUG: apiUrl =', apiUrl);
    console.log('🔍 DEBUG: apiUrl.startsWith("/") =', apiUrl.startsWith('/'));

    // If API URL is relative (starts with /), use current window origin
    // This allows WebSocket to work through Vite proxy
    if (apiUrl.startsWith('/')) {
      console.log(
        `🔍 WebSocket URL (using current origin for proxy): ${window.location.origin}`
      );
      return window.location.origin;
    }

    // Extract base URL from API URL (remove /v2/api or any path)
    const wsUrl = apiUrl.replace(/\/v2\/api.*$/, '');

    console.log(`🔍 WebSocket URL from API URL: ${wsUrl}`);
    return wsUrl;
  }, []);

  // Get JWT token from localStorage
  const getAuthToken = useCallback(() => {
    return localStorage.getItem('access_token');
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (socketRef.current?.connected) {
      console.log('WebSocket already connected');
      return;
    }

    const token = getAuthToken();
    if (!token) {
      console.warn('⚠️ No JWT token found - WebSocket connection may fail');
    } else {
      console.log(`🔑 JWT token found: ${token.substring(0, 20)}...`);
    }

    const wsUrl = getWebSocketUrl();
    const url = namespace ? `${wsUrl}${namespace}` : wsUrl;

    // Detect if we need to force polling transport (HTTPS frontend + HTTP backend = Mixed Content)
    const isHttpsFrontend = window.location.protocol === 'https:';
    const isHttpBackend = wsUrl.startsWith('http://');
    const forcePollingSolely = isHttpsFrontend && isHttpBackend;

    console.log(`🔌 Connecting to WebSocket: ${url}`);
    console.log(`🔐 Auth config:`, {
      token: token ? `${token.substring(0, 20)}...` : 'missing',
    });
    console.log(
      `🚦 Transport mode: ${forcePollingSolely ? 'POLLING ONLY (Mixed Content protection)' : 'WebSocket preferred'}`
    );

    const socket = io(url, {
      auth: {
        token: token || '',
      },
      query: {
        token: token || '', // ← Send token in query params too!
      },
      extraHeaders: {
        Authorization: token ? `Bearer ${token}` : '', // ← Send as HTTP header!
      },
      path: '/socket.io', // ← NO trailing slash!
      transports: forcePollingSolely ? ['polling'] : ['websocket', 'polling'], // ← Force polling if Mixed Content
      withCredentials: true, // ← REQUIRED for CORS!
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      ...socketOptions,
    });

    // Connection established
    socket.on('connect', () => {
      console.log(`✅ WebSocket connected: ${url} (ID: ${socket.id})`);
      setConnected(true);
    });

    // Disconnected
    socket.on('disconnect', (reason) => {
      console.log(`❌ WebSocket disconnected: ${url} (Reason: ${reason})`);
      setConnected(false);
    });

    // Connection error
    socket.on('connect_error', (error) => {
      console.error(`⚠️ WebSocket connection error: ${url}`, error.message);
      setConnected(false);
    });

    // Reconnection attempt
    socket.on('reconnect_attempt', (attemptNumber) => {
      console.log(`🔄 WebSocket reconnection attempt ${attemptNumber}: ${url}`);
    });

    // Reconnection successful
    socket.on('reconnect', (attemptNumber) => {
      console.log(
        `✅ WebSocket reconnected after ${attemptNumber} attempts: ${url}`
      );
      setConnected(true);
    });

    // Reconnection failed
    socket.on('reconnect_failed', () => {
      console.error(`❌ WebSocket reconnection failed: ${url}`);
      setConnected(false);
    });

    socketRef.current = socket;
  }, [namespace, getWebSocketUrl, getAuthToken, socketOptions]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (socketRef.current) {
      console.log('🔌 Disconnecting WebSocket');
      socketRef.current.disconnect();
      socketRef.current = null;
      setConnected(false);
    }
  }, []);

  // Reconnect with fresh token
  const reconnect = useCallback(() => {
    console.log('🔄 Reconnecting WebSocket with fresh token');
    disconnect();
    connect();
  }, [connect, disconnect]);

  // Auto-connect on mount if enabled
  useEffect(() => {
    if (!enabled || !autoConnect) return;

    // Use a flag to prevent cleanup from running if we're still mounted
    let isSubscribed = true;

    // Small delay to avoid rapid connect/disconnect in React StrictMode
    const timeoutId = setTimeout(() => {
      if (isSubscribed) {
        connect();
      }
    }, 100);

    return () => {
      isSubscribed = false;
      clearTimeout(timeoutId);

      // Only disconnect if we actually connected
      // React StrictMode will call this twice, but we prevent double disconnect
      if (socketRef.current?.connected) {
        disconnect();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, autoConnect]); // Don't include connect/disconnect - they're stable callbacks

  return {
    socket: socketRef.current,
    connected,
    connect,
    disconnect,
    reconnect,
  };
}
